import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CalendarBlank as CalendarIcon,
  ClockClockwise,
  Gavel,
  CircleNotch,
  CheckCircle,
  Info,
  Warning,
  CaretDown,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { InputField, SelectField } from '@/components/ui/form-field';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { useFormValidation, rules, type FieldValidation } from '@/hooks/useFormValidation';
import { useHearingsStore } from '@/lib/hearingsStore';
import { createHearing, updateHearing as apiUpdateHearing, checkHearingConflicts, type DiaryConflict } from '@/lib/hearingsApi';
import { uploadDocument } from '@/lib/documentsApi';
import { useMattersStore } from '@/lib/mattersStore';
import { useAuthStore } from '@/features/auth/store';
import { useDiaryStore } from '@/lib/diaryStore';
import { useBarristerBusy } from '@/lib/useBarristerBusy';
import { BarristerDayTimeline } from '@/components/shared/BarristerDayTimeline';
import { fetchCourts, type Court } from '@/lib/courtsApi';
import {
  HEARING_TYPES,
  HEARING_DURATIONS as DURATIONS,
  LEGAL_OUTCOME_TYPES,
  OUTCOME_NOTES_MAX,
  OUTCOME_SUMMARY_MIN,
} from '@/lib/hearingConstants';
import { nowLocalTime, todayLocalDate } from '@/lib/datetime';
import { DOCUMENT_ACCEPT_ATTR, DOCUMENT_EXTENSION_TO_MIME, DOCUMENT_FORMATS_LABEL } from '@jurivoxa/shared';
import { isMeaningfulTitle, isMeetingLink } from '@/lib/validators';
import { ScrollLock } from '@/components/shared/ScrollLock';

// ─── Types ────────────────────────────────────────────────────

type Mode = 'schedule' | 'record';

/** Every field this form validates. A type alias (not an interface) so the validation hook's
 *  `Record<string, unknown>` constraint is satisfied. */
type FormValues = {
  matterId: string;
  hearingType: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  courtroom: string;
  judge: string;
  videoLink: string;
  notes: string;
  outcome: string;
  outcomeNotes: string;
  orders: string;
};

// Outcome vocabulary is the canonical LEGAL_OUTCOME_TYPES from hearingConstants —
// one source consumed by every outcome screen (Rule #14 / HR-14). The old local
// list sent non-canonical values (won/settled/…) the backend map never matched,
// so recording a past outcome here silently failed to advance the matter status.

const HEARING_TYPE_API_MAP: Record<string, string> = {
  'First listing': 'first_listing',
  'CMC': 'case_management',
  'PTR': 'pre_trial_review',
  'Application': 'interlocutory',
  'Trial': 'trial',
  'Sentencing': 'sentencing',
  'Appeal': 'appeal',
  'Mention': 'mention',
  'Bail': 'bail',
  'Costs': 'costs',
  'Mediation': 'mediation',
  'Hearing': 'other',
  'PTPH': 'ptph',
};

// Field caps MIRROR apps/api/src/modules/hearings/hearings.schema.ts exactly, so this form can
// never accept something the server refuses for length — the user finds out here, by the field,
// not after a submit that reads like a fault.
const COURTROOM_MAX = 200;
const JUDGE_MAX = 200;
const VIDEO_LINK_MAX = 500;
const NOTES_MAX = 10_000;
const ORDERS_MAX = 10_000;

/** Multer's ceiling on POST /documents (apps/api/src/modules/documents/documents.routes.ts:30). */
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/**
 * Why the documents API would refuse this file — checked at the moment it is picked.
 * Previously an oversized or unsupported attachment failed inside a swallowed
 * `Promise.allSettled` and the user was still told the hearing was recorded with it.
 */
function uploadRejectionReason(file: File): string | null {
  const dot = file.name.lastIndexOf('.');
  const ext = dot === -1 ? '' : file.name.slice(dot).toLowerCase();
  const allowedMimes = DOCUMENT_EXTENSION_TO_MIME[ext];
  if (!allowedMimes) return `${file.name} — only ${DOCUMENT_FORMATS_LABEL} can be attached.`;
  if (file.type && !allowedMimes.includes(file.type)) {
    return `${file.name} — the file's contents do not match its ${ext} extension.`;
  }
  if (file.size > MAX_UPLOAD_BYTES) return `${file.name} — over the 50MB limit. Split or compress it.`;
  return null;
}

// ─── Component ────────────────────────────────────────────────

export default function NewHearingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const isBarrister = ['silk', 'junior', 'pupil', 'door_tenant'].includes(user?.memberType ?? '');
  const basePath = isClerk ? '/clerk' : isBarrister ? '/barrister' : '/admin';


  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  const fetchEvents = useDiaryStore((s) => s.fetchEvents);

  const matters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  useEffect(() => { fetchMatters(); }, [fetchMatters]);
  useEffect(() => { fetchHearings(); }, [fetchHearings]);

  const [courts, setCourts] = useState<Court[]>([]);
  const [courtsLoading, setCourtsLoading] = useState(true);
  useEffect(() => {
    fetchCourts()
      .then((c) => setCourts(c.filter((x) => x.isActive)))
      .catch(() => {})
      .finally(() => setCourtsLoading(false));
  }, []);

  const preselectedMatterId = searchParams.get('matter') ?? '';

  // ─── Mode — can be pre-set via ?mode=record in URL ───────────
  const [mode, setMode] = useState<Mode>(
    (searchParams.get('mode') as Mode) === 'record' ? 'record' : 'schedule'
  );

  // ─── Shared form state ────────────────────────────────────────
  const [matterId, setMatterId] = useState(preselectedMatterId);
  const [hearingType, setHearingType] = useState('trial');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState(''); // optional — defaults to 00:00 if left blank
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [courtId, setCourtId] = useState('');
  const [courtroom, setCourtroom] = useState('');
  const [judge, setJudge] = useState('');
  const [virtual, setVirtual] = useState(false);
  const [videoLink, setVideoLink] = useState('');
  const [notes, setNotes] = useState('');

  // ─── Record-mode outcome state ────────────────────────────────
  const [outcome, setOutcome] = useState('');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [orders, setOrders] = useState('');
  const [costs, setCosts] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  // Files the documents API would refuse, named at the moment they are picked.
  const [fileErrors, setFileErrors] = useState<string[]>([]);

  // Progressive disclosure — the court/venue block and the optional outcome detail ran this
  // form past two screens. Each section forces itself open the moment it holds anything, so
  // nothing a user typed can end up hidden behind a collapsed heading.
  const [showVenue, setShowVenue] = useState(false);
  const [showMoreOutcome, setShowMoreOutcome] = useState(false);
  const [showPrepNotes, setShowPrepNotes] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [conflictPrompt, setConflictPrompt] = useState(false);
  const [conflictList, setConflictList] = useState<DiaryConflict[]>([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  // ─── Derived ──────────────────────────────────────────────────
  const selectedMatter = matters.find((m) => m.id === matterId);
  const selectedCourt = courts.find((c) => c.id === courtId);
  const hearingTypeLabel = HEARING_TYPES.find((t) => t.value === hearingType)?.label ?? hearingType;

  // Resolved time — blank means "time not known"; sent as 00:00 to the API
  const resolvedTime = startTime || '00:00';

  // Each disclosure is open when asked for, or when it already holds something.
  const venueOpen = showVenue || !!courtId || !!courtroom.trim() || !!judge.trim() || virtual;
  const moreOutcomeOpen =
    showMoreOutcome || !!orders.trim() || !!costs || !!notes.trim() || attachedFiles.length > 0;
  const prepNotesOpen = showPrepNotes || !!notes.trim();

  // Local today, read every render. The old module-level constant was UTC (so it named
  // yesterday for part of each day east of Greenwich) and never changed after midnight.
  const today = todayLocalDate();

  // Past-time guard (schedule mode only — record mode is for past hearings).
  // The rule itself lives in `validations` below, so it answers with a message on the field
  // rather than silently disabling the button.
  const isSchedulingToday = mode === 'schedule' && date === today;

  // ─── Conflict detection ────────────────────────────────────────
  // Only meaningful in schedule mode when barrister and date are known
  const barristerId = selectedMatter?.leadBarristerUserId ?? null;

  // Compute ISO start for conflict check — only when start time is explicitly set
  const conflictStartIso = useMemo(() => {
    if (!date || !startTime) return null;
    return new Date(`${date}T${startTime}`).toISOString();
  }, [date, startTime]);

  // Fetch events for the selected date (use noon to ensure full-day coverage)
  const dateMidIso = useMemo(() => {
    if (!date) return null;
    return new Date(`${date}T12:00`).toISOString();
  }, [date]);

  const { getConflicts, getEventsForDate } = useBarristerBusy(
    mode === 'schedule' ? barristerId : null,
    dateMidIso ? [dateMidIso] : [],
  );
  const hearingConflicts = useMemo(
    () => (conflictStartIso ? getConflicts(conflictStartIso, durationMinutes) : []),
    [conflictStartIso, durationMinutes, getConflicts],
  );
  const dayEvents = useMemo(
    () => (dateMidIso ? getEventsForDate(dateMidIso) : []),
    [dateMidIso, getEventsForDate],
  );

  // When switching mode, reset date + outcome fields (time keeps its value)
  const handleModeChange = (m: Mode) => {
    setMode(m);
    setDate('');
    setOutcome('');
    setOutcomeNotes('');
    setOrders('');
    setCosts('');
  };

  // ─── Validation ───────────────────────────────────────────────
  const formValues = useMemo<FormValues>(
    () => ({
      matterId, hearingType, date, startTime, durationMinutes,
      courtroom, judge, videoLink, notes,
      outcome, outcomeNotes, orders,
    }),
    [matterId, hearingType, date, startTime, durationMinutes,
     courtroom, judge, videoLink, notes, outcome, outcomeNotes, orders],
  );

  /**
   * Every field states its own rule and carries its own message.
   *
   * Required fields answer on a submit attempt; a format or range problem answers as soon as
   * the field is left — the contract the New Contact and New Matter forms use. Rules that only
   * apply to one mode (an outcome is meaningless on a hearing that has not happened) are added
   * per mode, so the form is never judged against fields it is not showing.
   */
  const validations = useMemo<FieldValidation<FormValues>>(() => {
    const v: FieldValidation<FormValues> = {
      matterId: [{ ...rules.required('Matter'), message: 'Choose the matter this hearing belongs to.' }],
      hearingType: [{ ...rules.required('Hearing type'), message: 'Choose the type of hearing.' }],
      date: [
        { ...rules.required('Date'), message: 'Enter the date of the hearing.' },
        { ...rules.date('Date'), message: 'That is not a date Jurivoxa can read.' },
        mode === 'schedule'
          ? rules.custom<FormValues>(
              (value) => !value || String(value) >= today,
              'A hearing being scheduled must be dated today or later. To log one that already happened, switch to Record Past Hearing.',
            )
          : rules.custom<FormValues>(
              (value) => !value || String(value) <= today,
              'A hearing that already happened cannot carry a future date. To list a future date, switch to Schedule Hearing.',
            ),
      ],
      startTime: [
        mode === 'schedule'
          ? rules.custom<FormValues>(
              (value, values) => !value || values.date !== today || String(value) >= nowLocalTime(),
              'That time has already passed today — pick a later time.',
            )
          : rules.custom<FormValues>(
              (value, values) => !value || values.date !== today || String(value) <= nowLocalTime(),
              'That time is still ahead today — a hearing that already happened needs a time that has already passed.',
            ),
      ],
      durationMinutes: [
        rules.custom<FormValues>(
          (value) => Number(value) > 0 && Number(value) <= 24 * 60,
          'Estimated duration must be between 1 minute and 24 hours.',
        ),
      ],
      courtroom: [{ ...rules.maxLength('Courtroom', COURTROOM_MAX), message: `Courtroom must be ${COURTROOM_MAX} characters or fewer.` }],
      judge: [{ ...rules.maxLength('Judge', JUDGE_MAX), message: `Judge must be ${JUDGE_MAX} characters or fewer.` }],
      notes: [{ ...rules.maxLength('Notes', NOTES_MAX), message: `Notes must be ${NOTES_MAX.toLocaleString()} characters or fewer.` }],
    };

    // A virtual hearing with no link is counsel with nowhere to appear.
    if (virtual) {
      v.videoLink = [
        { ...rules.required('Meeting link'), message: 'A virtual hearing needs the link counsel will join.' },
        rules.custom<FormValues>(
          (value) => !String(value ?? '').trim() || isMeetingLink(String(value)),
          'Enter the full joining link, starting with https://',
        ),
        { ...rules.maxLength('Meeting link', VIDEO_LINK_MAX), message: `The meeting link must be ${VIDEO_LINK_MAX} characters or fewer.` },
      ];
    }

    if (mode === 'record') {
      v.outcome = [{ ...rules.required('Result'), message: 'Choose what the hearing decided.' }];
      v.outcomeNotes = [
        { ...rules.required('What happened'), message: 'Record what happened at the hearing.' },
        {
          ...rules.minLength('What happened', OUTCOME_SUMMARY_MIN),
          message: `Give at least ${OUTCOME_SUMMARY_MIN} characters — this is what the next person to open the matter will read.`,
        },
        rules.custom<FormValues>(
          (value) => isMeaningfulTitle(String(value ?? '')),
          'Describe the outcome in words — a repeated character is not a summary.',
        ),
      ];
      v.orders = [{ ...rules.maxLength('Orders made', ORDERS_MAX), message: `Orders made must be ${ORDERS_MAX.toLocaleString()} characters or fewer.` }];
    }

    return v;
  }, [mode, virtual, today]);

  const { errors, validateAll, validateField, getFieldProps } = useFormValidation(formValues, { validations });

  // Reading order of the form, so a refused submit lands on the first thing to fix instead of
  // leaving the user to hunt for the red field in a two-screen form.
  const FIELD_ANCHORS: [keyof FormValues, string][] = [
    ['matterId', 'hf-matter'],
    ['hearingType', 'hf-type'],
    ['date', 'hf-date'],
    ['startTime', 'hf-start-time'],
    ['durationMinutes', 'hf-duration'],
    ['courtroom', 'hf-courtroom'],
    ['judge', 'hf-judge'],
    ['videoLink', 'hf-video-link'],
    ['outcome', 'hf-outcome'],
    ['outcomeNotes', 'hf-outcome-notes'],
    ['orders', 'hf-orders'],
    ['notes', 'hf-notes'],
  ];

  const focusFirstError = () => {
    for (const [field, anchor] of FIELD_ANCHORS) {
      if (validateField(field)) {
        document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
  };

  // ─── Handlers ─────────────────────────────────────────────────

  // Submit gate: ask the backend (authoritative, covers Google) whether the proposed time
  // clashes. Warn first — never block; the barrister explicitly confirms to proceed.
  const handleSchedule = async () => {
    if (!validateAll()) {
      toast.error('Please fix the highlighted fields before scheduling.');
      focusFirstError();
      return;
    }
    setCheckingConflicts(true);
    try {
      const conflicts = await checkHearingConflicts({
        caseId: matterId,
        startAt: `${date}T${resolvedTime}:00Z`,
        durationMinutes,
      });
      if (conflicts.length > 0) {
        setConflictList(conflicts);
        setConflictPrompt(true);
        return;
      }
    } catch {
      // If the check itself fails, don't block scheduling — fall through to save.
    } finally {
      setCheckingConflicts(false);
    }
    void doSchedule();
  };

  const doSchedule = async () => {
    setConflictPrompt(false);
    setSubmitting(true);
    try {
      await createHearing({
        caseId: matterId,
        hearingDate: `${date}T${resolvedTime}:00Z`,
        durationMinutes,
        hearingType: HEARING_TYPE_API_MAP[hearingTypeLabel] ?? 'other',
        courtId: courtId || undefined,
        courtroom: courtroom || undefined,
        judge: judge || undefined,
        notes: notes || undefined,
        isVirtual: virtual,
        videoLink: videoLink || undefined,
      });
      await fetchHearings();
      fetchEvents();
      toast.success('Hearing scheduled');
      navigate(preselectedMatterId ? `${basePath}/matters/${preselectedMatterId}` : `${basePath}/hearings`);
    } catch (err: any) {
      // Surface the server's actual refusal — a duplicate listing, a past date, a closed matter.
      // A deliberate rejection reported as "please try again" reads as a fault in the product.
      toast.error(err?.response?.data?.error?.message ?? 'Could not schedule hearing — please try again');
      setSubmitting(false);
    }
  };

  const handleRecord = async () => {
    if (!validateAll()) {
      toast.error('Please fix the highlighted fields before recording.');
      focusFirstError();
      return;
    }
    if (fileErrors.length > 0) {
      toast.error('Remove or replace the attachments that cannot be uploaded.');
      return;
    }
    setSubmitting(true);
    try {
      // Step 1 — create the hearing record
      const created = await createHearing({
        caseId: matterId,
        hearingDate: `${date}T${resolvedTime}:00Z`,
        durationMinutes,
        hearingType: HEARING_TYPE_API_MAP[hearingTypeLabel] ?? 'other',
        courtId: courtId || undefined,
        courtroom: courtroom || undefined,
        judge: judge || undefined,
        notes: notes || undefined,
        isVirtual: virtual,
        videoLink: videoLink || undefined,
        backdated: true, // recording an already-happened hearing — allow past date, no reminders
      });

      // Step 2 — immediately mark it completed with the outcome details
      await apiUpdateHearing(created.id, {
        status: 'completed',
        outcome: outcomeNotes.trim(),
        outcomeType: outcome,
        courtOrders: orders.trim() || undefined,
        costsPosition: costs || undefined,
      });

      // Step 3 — upload any attached documents linked to this hearing.
      // allSettled keeps one bad file from losing the rest, but the failures are COUNTED and
      // reported: silently swallowing them told the user a sealed order was filed when it wasn't.
      let failedUploads = 0;
      if (attachedFiles.length > 0) {
        const uploads = await Promise.allSettled(
          attachedFiles.map((file) =>
            uploadDocument(file, {
              caseId: matterId,
              hearingId: created.id,
              category: 'court_order',
            })
          )
        );
        failedUploads = uploads.filter((u) => u.status === 'rejected').length;
      }

      // Step 4 — refresh the store so the new hearing appears everywhere
      await fetchHearings();

      toast.success('Past hearing recorded');
      if (failedUploads > 0) {
        toast.error(
          `${failedUploads} of ${attachedFiles.length} document${attachedFiles.length > 1 ? 's' : ''} could not be attached. ` +
          `Add ${failedUploads > 1 ? 'them' : 'it'} from the matter's Documents tab.`,
        );
      }
      navigate(preselectedMatterId ? `${basePath}/matters/${preselectedMatterId}` : `${basePath}/hearings`);
    } catch (err: any) {
      // Surface the real server reason (e.g. a conflict) instead of a generic message.
      toast.error(err?.response?.data?.error?.message ?? 'Could not record hearing — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────

  // ONE submit control, rendered twice: in the sticky rail at lg and up, and in the sticky
  // bar below lg. Defined once so the two can never drift apart.
  const renderSubmit = (className: string) => (
    <Button
      variant={mode === 'schedule' ? 'primary' : 'outline'}
      size="md"
      className={`${className} ${mode === 'record' ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200 dark:hover:bg-amber-500/25' : ''}`}
      disabled={submitting || checkingConflicts}
      leftIcon={
        submitting || checkingConflicts
          ? <CircleNotch className="h-4 w-4 animate-spin" />
          : mode === 'schedule'
          ? <CalendarIcon className="h-4 w-4" />
          : <ClockClockwise className="h-4 w-4" />
      }
      onClick={mode === 'schedule' ? handleSchedule : handleRecord}
    >
      {submitting
        ? mode === 'schedule' ? 'Scheduling…' : 'Recording…'
        : mode === 'schedule' ? 'Schedule hearing' : 'Record past hearing'
      }
    </Button>
  );

  return (
    <div>
      <PageHeader
        title={mode === 'schedule' ? 'Schedule Hearing' : 'Record Past Hearing'}
        onBack={() => navigate(-1)}
        backLabel={preselectedMatterId ? 'Back to matter' : 'Back to hearings'}
      />

      {/* ── Mode toggle ── */}
      <div className="mb-4 flex gap-2 rounded-xl border border-border bg-muted/30 p-1">
        <button
          type="button"
          onClick={() => handleModeChange('schedule')}
          className={`flex flex-1 items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-all ${
            mode === 'schedule'
              ? 'bg-white shadow-sm dark:bg-card'
              : 'hover:bg-muted/50'
          }`}
        >
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
            mode === 'schedule' ? 'bg-brand-600 text-white' : 'bg-muted text-muted-foreground'
          }`}>
            <CalendarIcon className="h-4 w-4" />
          </div>
          <div>
            <div className={`text-sm font-semibold ${mode === 'schedule' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Schedule Hearing
            </div>
            <div className="text-[11px] text-muted-foreground">Adds to the diary, sends reminders</div>
          </div>
          {mode === 'schedule' && (
            <CheckCircle className="ml-auto h-4 w-4 text-brand-600 dark:text-brand-400" weight="fill" />
          )}
        </button>

        <button
          type="button"
          onClick={() => handleModeChange('record')}
          className={`flex flex-1 items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-all ${
            mode === 'record'
              ? 'bg-white shadow-sm dark:bg-card'
              : 'hover:bg-muted/50'
          }`}
        >
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
            mode === 'record' ? 'bg-amber-600 text-white' : 'bg-muted text-muted-foreground'
          }`}>
            <ClockClockwise className="h-4 w-4" />
          </div>
          <div>
            <div className={`text-sm font-semibold ${mode === 'record' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Record Past Hearing
            </div>
            <div className="text-[11px] text-muted-foreground">Already happened — no reminders</div>
          </div>
          {mode === 'record' && (
            <CheckCircle className="ml-auto h-4 w-4 text-amber-600 dark:text-amber-400" weight="fill" />
          )}
        </button>
      </div>

      {/* ── Info banner for record mode ── */}
      {mode === 'record' && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-500/10">
          <Info className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" weight="duotone" />
          <div className="text-xs text-amber-800 dark:text-amber-300">
            <span className="font-semibold">Historical hearing.</span>{' '}
            Logged to the matter timeline with its outcome — no diary entry, no reminders.
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* ── Left column ── */}
        {/* `min-w-0`: a grid item defaults to `min-width: auto` and refuses to shrink below its
            content, so on a phone both columns measured 480px inside a 390px viewport and every
            field ran off the right edge with no way to scroll to it. */}
        <div className="min-w-0 space-y-6">

          {/* Hearing details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-brand-600 dark:text-brand-400" /> Hearing Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 [&>*]:min-w-0 sm:grid-cols-2">

              {/* Matter — locked when navigated from a matter page */}
              <div className="sm:col-span-2" id="hf-matter">
                {preselectedMatterId && selectedMatter ? (
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      Matter
                    </label>
                    <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {selectedMatter.number} · {selectedMatter.name}
                        </div>
                        {(selectedMatter.area || selectedMatter.barrister) && (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {selectedMatter.area}{selectedMatter.barrister ? ` · ${selectedMatter.barrister}` : ''}
                          </div>
                        )}
                      </div>
                      <span className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-600 dark:text-brand-400">
                        Pre-selected
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <SelectField
                      label="Matter"
                      value={matterId}
                      onChange={(e) => setMatterId(e.target.value)}
                      required
                      {...getFieldProps('matterId')}
                    >
                      <option value="">Select matter…</option>
                      {matters.map((m) => (
                        <option key={m.id} value={m.id}>{m.number} · {m.name}</option>
                      ))}
                    </SelectField>
                    {selectedMatter && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {selectedMatter.area}{selectedMatter.barrister ? ` · ${selectedMatter.barrister}` : ''}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Type */}
              <div id="hf-type">
                <SelectField
                  label="Hearing type"
                  value={hearingType}
                  onChange={(e) => setHearingType(e.target.value)}
                  required
                  {...getFieldProps('hearingType')}
                >
                  {HEARING_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </SelectField>
              </div>

              {/* Date — restricted to future for schedule, past/today for record */}
              <div id="hf-date">
                <InputField
                  type="date"
                  label="Date"
                  value={date}
                  min={mode === 'record' ? undefined : today}
                  max={mode === 'record' ? today : undefined}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  hint={mode === 'schedule' ? 'Today or any future date.' : 'Today or any past date.'}
                  {...getFieldProps('date')}
                />
              </div>

              {/* Time — optional, but it cannot contradict the mode: a past hearing listed
                  for 6pm today has not happened yet. */}
              <div id="hf-start-time">
                <InputField
                  type="time"
                  label="Start time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  hint="Optional — leave blank if the listing time is not known"
                  min={isSchedulingToday ? nowLocalTime() : undefined}
                  max={mode === 'record' && date === today ? nowLocalTime() : undefined}
                  step={900}
                  {...getFieldProps('startTime')}
                />
              </div>

              {/* Duration */}
              <div id="hf-duration">
                <SelectField
                  label="Estimated duration"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Number(e.target.value))}
                  {...getFieldProps('durationMinutes')}
                >
                  {DURATIONS.map((d) => (
                    <option key={d.minutes} value={d.minutes}>{d.label}</option>
                  ))}
                </SelectField>
              </div>

              {/* Barrister schedule conflict check — schedule mode + barrister known + date set */}
              {mode === 'schedule' && date && barristerId && (
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    {selectedMatter?.barrister
                      ? `${selectedMatter.barrister}'s schedule`
                      : 'Barrister schedule'}
                  </label>
                  <BarristerDayTimeline
                    events={dayEvents}
                    proposedStart={conflictStartIso}
                    proposedDurationMinutes={conflictStartIso ? durationMinutes : 0}
                    conflicts={hearingConflicts}
                  />
                  {!startTime && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Add a start time above to check for scheduling conflicts.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Court & venue — every field optional, so it stays folded until asked for */}
          <Card>
            <button
              type="button"
              onClick={() => setShowVenue((v) => !v)}
              className="flex w-full items-center justify-between px-5 py-3 text-left"
            >
              {/* The hint sat BESIDE the title on one line, so on a phone "Court & judge" broke
                  across two lines with the hint wrapping beside it. Stacked below `sm`, side by
                  side from `sm` up where there is room. */}
              <span className="flex min-w-0 flex-col gap-0.5 text-sm font-semibold text-foreground sm:flex-row sm:items-center sm:gap-2">
                <span className="flex items-center gap-2">
                  <Gavel className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" /> Court &amp; judge
                </span>
                <span className="text-xs font-normal text-muted-foreground">
                  {venueOpen ? '' : 'court, room, judge, video link — all optional'}
                </span>
              </span>
              <CaretDown className={`h-4 w-4 text-muted-foreground transition-transform ${venueOpen ? 'rotate-180' : ''}`} />
            </button>
            {venueOpen && (
            <CardContent className="grid gap-4 border-t border-border pt-4 [&>*]:min-w-0 sm:grid-cols-2">
              <SelectField
                label="Court"
                value={courtId}
                onChange={(e) => setCourtId(e.target.value)}
                disabled={courtsLoading}
                hint="Optional"
              >
                <option value="">{courtsLoading ? 'Loading…' : 'Select court…'}</option>
                {courts.map((c) => (
                  <option key={c.id} value={c.id}>{c.shortName} — {c.name}</option>
                ))}
              </SelectField>

              <div id="hf-courtroom">
                <InputField
                  label="Courtroom"
                  placeholder="e.g. Court 4"
                  value={courtroom}
                  onChange={(e) => setCourtroom(e.target.value)}
                  maxLength={COURTROOM_MAX}
                  hint="Optional"
                  {...getFieldProps('courtroom')}
                />
              </div>

              <div id="hf-judge">
                <InputField
                  label="Judge"
                  placeholder="e.g. Smith J"
                  value={judge}
                  onChange={(e) => setJudge(e.target.value)}
                  maxLength={JUDGE_MAX}
                  hint="Optional"
                  {...getFieldProps('judge')}
                />
              </div>

              <div className="flex flex-col justify-end gap-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={virtual}
                    onChange={(e) => setVirtual(e.target.checked)}
                    className="h-4 w-4 rounded border-border accent-brand-600"
                  />
                  Virtual hearing
                </label>
                {virtual && (
                  <div id="hf-video-link">
                    <InputField
                      label="Meeting link"
                      type="url"
                      inputMode="url"
                      placeholder="https://teams.microsoft.com/…"
                      value={videoLink}
                      onChange={(e) => setVideoLink(e.target.value)}
                      maxLength={VIDEO_LINK_MAX}
                      required
                      {...getFieldProps('videoLink')}
                    />
                  </div>
                )}
              </div>
            </CardContent>
            )}
          </Card>

          {/* Preparation notes (schedule only) — optional, so folded until asked for */}
          {mode === 'schedule' && (
            <Card>
              <button
                type="button"
                onClick={() => setShowPrepNotes((v) => !v)}
                className="flex w-full items-center justify-between px-5 py-3 text-left"
              >
                <span className="text-sm font-semibold text-foreground">
                  Preparation notes
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">(optional)</span>
                </span>
                <CaretDown className={`h-4 w-4 text-muted-foreground transition-transform ${prepNotesOpen ? 'rotate-180' : ''}`} />
              </button>
              {prepNotesOpen && (
                <CardContent id="hf-notes" className="border-t border-border pt-4">
                  <ExpandableTextarea
                    label="Preparation notes"
                    rows={3}
                    placeholder="Listing directions, instructions for counsel, bundle references…"
                    value={notes}
                    onChange={setNotes}
                    maxLength={NOTES_MAX}
                  />
                  {errors.notes && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.notes}</p>}
                </CardContent>
              )}
            </Card>
          )}

          {/* Outcome section (record mode only) */}
          {mode === 'record' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" weight="duotone" />
                  Outcome
                  <Badge variant="danger" className="ml-1">Required</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">

                {/* Outcome selector */}
                <div id="hf-outcome">
                  <SelectField
                    label="Result"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    required
                    {...getFieldProps('outcome')}
                  >
                    <option value="">— What was the result? —</option>
                    {LEGAL_OUTCOME_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </SelectField>
                </div>

                {/* Outcome summary */}
                <div id="hf-outcome-notes">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    What happened <span className="text-red-500">*</span>
                  </label>
                  <ExpandableTextarea
                    label="What happened"
                    rows={3}
                    value={outcomeNotes}
                    onChange={setOutcomeNotes}
                    placeholder="e.g. Judge granted summary judgment. Defence had no real prospect of success."
                    maxLength={OUTCOME_NOTES_MAX}
                  />
                  {/* The message used to appear only once something had been typed, so an empty
                      field just disabled the button with no explanation. */}
                  {errors.outcomeNotes
                    ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.outcomeNotes}</p>
                    : <p className="mt-1 text-xs text-muted-foreground">At least {OUTCOME_SUMMARY_MIN} characters.</p>}
                </div>

                {/* Everything below is optional detail — one line instead of four fields */}
                <button
                  type="button"
                  onClick={() => setShowMoreOutcome((v) => !v)}
                  className="flex w-full items-center justify-between rounded-lg border border-border bg-muted/20 px-3 py-2 text-left"
                >
                  <span className="text-xs font-semibold text-foreground">
                    Orders, costs, notes &amp; documents
                    <span className="ml-1.5 font-normal text-muted-foreground">(optional)</span>
                  </span>
                  <CaretDown className={`h-4 w-4 text-muted-foreground transition-transform ${moreOutcomeOpen ? 'rotate-180' : ''}`} />
                </button>

                {moreOutcomeOpen && (
                <>
                {/* Orders */}
                <div id="hf-orders">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Orders made
                  </label>
                  <ExpandableTextarea
                    label="Orders made"
                    rows={2}
                    value={orders}
                    onChange={setOrders}
                    placeholder="e.g. Defence statement to be served by 28 June. Trial estimate: 5 days."
                    maxLength={ORDERS_MAX}
                  />
                  {errors.orders && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.orders}</p>}
                </div>

                {/* Costs position */}
                <SelectField
                  label="Costs position"
                  value={costs}
                  onChange={(e) => setCosts(e.target.value)}
                  hint="Optional"
                >
                  <option value="">— Select —</option>
                  <option value="Costs awarded to client">Costs to client</option>
                  <option value="Costs awarded to other side">Costs to other side</option>
                  <option value="Costs reserved">Costs reserved</option>
                  <option value="Costs in the case">Costs in the case</option>
                  <option value="No order as to costs">No order as to costs</option>
                  <option value="Summary assessment">Summary assessment</option>
                  <option value="Detailed assessment">Detailed assessment</option>
                </SelectField>

                {/* Notes */}
                <div id="hf-notes">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Additional notes
                  </label>
                  <ExpandableTextarea
                    label="Additional notes"
                    rows={2}
                    value={notes}
                    onChange={setNotes}
                    placeholder="Any other observations or follow-up actions…"
                    maxLength={NOTES_MAX}
                  />
                  {errors.notes && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.notes}</p>}
                </div>

                {/* Document upload — attach court orders or hearing documents */}
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Attach documents <span className="font-normal normal-case text-muted-foreground">(optional — court orders, sealed orders, etc.)</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground hover:border-brand-400 hover:bg-brand-50/30 transition-colors">
                    <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M10 3v10M6 7l4-4 4 4" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 17h14" strokeLinecap="round"/>
                    </svg>
                    <span>{attachedFiles.length === 0 ? 'Click to attach files' : `${attachedFiles.length} file${attachedFiles.length > 1 ? 's' : ''} selected`}</span>
                    <input
                      type="file"
                      multiple
                      accept={DOCUMENT_ACCEPT_ATTR}
                      className="sr-only"
                      onChange={(e) => {
                        const picked = Array.from(e.target.files ?? []);
                        const rejected = picked
                          .map(uploadRejectionReason)
                          .filter((r): r is string => r !== null);
                        const accepted = picked.filter((f) => uploadRejectionReason(f) === null);
                        setFileErrors(rejected);
                        if (accepted.length > 0) setAttachedFiles((prev) => [...prev, ...accepted]);
                        // Clear the input so the same file can be re-picked once it is fixed.
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {fileErrors.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {fileErrors.map((msg, i) => (
                        <li key={i} className="rounded-md bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">
                          {msg}
                        </li>
                      ))}
                    </ul>
                  )}
                  {attachedFiles.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {attachedFiles.map((f: File, i: number) => (
                        <li key={i} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-1.5 text-xs">
                          <span className="truncate text-foreground">{f.name}</span>
                          <button
                            type="button"
                            onClick={() => setAttachedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                            className="ml-2 shrink-0 text-muted-foreground hover:text-danger"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* ── Right sidebar ──
            At lg and up the rail is sticky AND bounded to the window: the cards scroll inside
            it, the submit button does not, so the action can never sit below the fold on a
            short screen. Below lg the rail is in normal flow at the end of a form two screens
            long, so the same summary and action are repeated in the sticky bar after the grid. */}
        <div className="min-w-0 space-y-4 lg:sticky lg:top-20 lg:flex lg:max-h-[calc(100vh-6rem)] lg:flex-col lg:gap-4 lg:space-y-0 lg:self-start">
          <div className="min-h-0 space-y-4 lg:flex-1 lg:overflow-y-auto">
          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {[
                ['Matter', selectedMatter ? `${selectedMatter.number} · ${selectedMatter.name}` : '—'],
                ['Type', hearingTypeLabel],
                ['Date', date || '—'],
                ['Time', startTime || '—'],
                ['Duration', DURATIONS.find((d) => d.minutes === durationMinutes)?.label ?? '—'],
                ['Court', selectedCourt?.shortName ?? '—'],
                ['Judge', judge || '—'],
                ...(mode === 'record' && outcome ? [['Outcome', LEGAL_OUTCOME_TYPES.find(o => o.value === outcome)?.label ?? outcome]] : []),
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="text-right font-medium text-foreground">{val}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* What happens */}
          <Card>
            <CardContent className="p-4">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {mode === 'schedule' ? 'On scheduling' : 'On recording'}
              </div>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                {mode === 'schedule' ? (
                  <>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-brand-500" weight="fill" />Added to diary</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-brand-500" weight="fill" />24h reminder queued</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-brand-500" weight="fill" />Timeline event written</li>
                  </>
                ) : (
                  <>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-amber-500" weight="fill" />Hearing logged with outcome</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-amber-500" weight="fill" />Matter timeline updated</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-amber-500" weight="fill" />No diary or reminder created</li>
                  </>
                )}
              </ul>
            </CardContent>
          </Card>

          </div>

          {/* Submit — outside the scrolling part of the rail. On lg+ the rail is a sticky column,
              so this sits in view the whole time; below lg it is the end of the form. A second,
              floating copy used to follow the page down on narrow screens and was removed. */}
          {renderSubmit('w-full shrink-0')}
        </div>
      </div>

      {conflictPrompt && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setConflictPrompt(false)}>
          <ScrollLock />
          <Card className="my-auto w-full max-w-md shadow-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 border-b border-border px-6 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-amber-100 dark:bg-amber-500/15">
                <Warning className="h-5 w-5 text-amber-600 dark:text-amber-400" weight="fill" />
              </div>
              <div>
                <div className="text-base font-semibold text-foreground">Scheduling conflict</div>
                <div className="text-xs text-muted-foreground">
                  This time clashes with {conflictList.length} event{conflictList.length > 1 ? 's' : ''} in the diary.
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <ul className="space-y-2.5">
                {conflictList.map((c, i) => {
                  const start = new Date(c.startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                  const end = new Date(c.endAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                  return (
                    <li key={i} className="rounded-lg border border-border/60 bg-muted/20 px-3.5 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground">{c.title}</span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${c.kind === 'overlap' ? 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300' : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'}`}>
                          {c.kind === 'overlap' ? 'Overlaps' : 'Tight gap'}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {start}–{end}
                        {c.kind === 'adjacent' && ` · only ${c.gapMinutes} min between them`}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">
                Jurivoxa won't block you — confirm only if the clash is intentional.
              </p>
            </CardContent>
            <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
              <Button variant="outline" size="md" onClick={() => setConflictPrompt(false)}>Change time</Button>
              <Button variant="primary" size="md" onClick={() => void doSchedule()}>Schedule anyway</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
